/**
 * AnalysisResultsPage Component
 * Displays analysis results with polling progress indicator and result cards
 *
 * This module contains:
 * - AnalysisResultsPageV1: The standard analysis results page
 * - AnalysisResultsRouter: Routes between V1 and V2 based on payload parameter (default export)
 */

import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { ArrowLeft, Loader2, AlertCircle, LockOpen, Users, Package, MapPin, Search, X, ChevronDown, MessageSquare, Target, Lightbulb, Share2, Crown, Mail, Plus, Trash2, CheckCircle2, RefreshCw } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { analysesService } from '@/services/api/analyses';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DashboardHeader } from '@/components/layout/DashboardHeader';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@/components/common/GlassCard/GlassCard';
import { ProgressBanner } from '@/components/results/ProgressBanner/ProgressBanner';
import { VertriebsgebietCardContent } from '@/components/results/VertriebsgebietCardContent';
import { VertriebsgebietOverlayContent } from '@/components/results/VertriebsgebietOverlayContent';
import { ZielgruppeCardContent } from '@/components/results/ZielgruppeCardContent';
import { TargetGroupOverlayContent } from '@/components/results/TargetGroupOverlayContent';
import { ProduktCardContent } from '@/components/results/ProduktCardContent';
import { ProduktOverlayContent } from '@/components/results/ProduktOverlayContent';
import { KommunikationCardContent } from '@/components/results/KommunikationCardContent';
import { KommunikationOverlayContent } from '@/components/results/KommunikationOverlayContent';
import { MassnahmenCardContent } from '@/components/results/MassnahmenCardContent';
import { MassnahmenOverlayContent } from '@/components/results/MassnahmenOverlayContent';
import { HandlungsempfehlungenCardContent } from '@/components/results/HandlungsempfehlungenCardContent';
import { HandlungsempfehlungenOverlayContent } from '@/components/results/HandlungsempfehlungenOverlayContent';
import { ShareAnalysisModal } from '@/components/results/ShareAnalysisModal';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useRequestStatus } from '@/hooks/useRequestStatus';
import { useAuth } from '@/hooks/useAuth';
import { useSectionDescriptions } from '@/hooks/useSectionDescriptions';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import type { GetDescriptionFn } from '@/types/sectionDescription';
import { parseVertriebsgebietData } from '@/types/vertriebsgebiet';
import { parseZielgruppeData } from '@/types/zielgruppe';
import { parseProduktData } from '@/types/produkt';
import { parseKommunikationData } from '@/types/kommunikation';
import { parseMassnahmenData } from '@/types/massnahmen';
import { parseHandlungsempfehlungenData } from '@/types/handlungsempfehlungen';
import { requestsService, type FeatureResult } from '@/services/api/requests';
import type { VertriebsgebietFullData } from '@/types/vertriebsgebiet';
import type { ZielgruppeFullData } from '@/types/zielgruppe';
import type { ProduktFullData } from '@/types/produkt';
import type { KommunikationFullData } from '@/types/kommunikation';
import type { MassnahmenFullData } from '@/types/massnahmen';
import type { HandlungsempfehlungenFullData } from '@/types/handlungsempfehlungen';
import { env } from '@/utils/env';

/**
 * Card configuration for the 6 result cards
 * Feature IDs are configurable via environment variables
 */
const CARD_CONFIGS = [
  {
    id: env.analysis.featureNames.massnahmen,
    icon: Target,
    translationKey: 'massnahmen',
  },
  {
    id: env.analysis.featureNames.vertriebsgebiet,
    icon: MapPin,
    translationKey: 'vertriebsgebiet',
  },
  {
    id: env.analysis.featureNames.zielgruppe,
    icon: Users,
    translationKey: 'targetGroup',
  },
  {
    id: env.analysis.featureNames.produkt,
    icon: Package,
    translationKey: 'produkt',
  },
  {
    id: env.analysis.featureNames.kommunikation,
    icon: MessageSquare,
    translationKey: 'kommunikation',
  },
  {
    id: env.analysis.featureNames.handlungsempfehlungen,
    icon: Lightbulb,
    translationKey: 'handlungsempfehlungen',
  },
];


/**
 * Detail overlay component for showing extended information
 * Covers all cards in the grid
 */
function DetailOverlay({
  config,
  feature: _feature,
  onClose,
  t,
  vertriebsgebietData,
  zielgruppeData,
  produktData,
  kommunikationData,
  massnahmenData,
  handlungsempfehlungenData,
  getDescription,
  onPremiumClick,
}: {
  config: typeof CARD_CONFIGS[0];
  feature?: FeatureResult;
  onClose: () => void;
  t: (key: string) => string;
  vertriebsgebietData?: VertriebsgebietFullData | null;
  zielgruppeData?: ZielgruppeFullData | null;
  produktData?: ProduktFullData | null;
  kommunikationData?: KommunikationFullData | null;
  massnahmenData?: MassnahmenFullData | null;
  handlungsempfehlungenData?: HandlungsempfehlungenFullData | null;
  getDescription?: GetDescriptionFn;
  onPremiumClick?: () => void;
}) {
  const Icon = config.icon;
  const title = t(`results.cards.${config.translationKey}.title`);
  const isVertriebsgebiet = config.id === env.analysis.featureNames.vertriebsgebiet;
  const isTargetGroup = config.id === env.analysis.featureNames.zielgruppe;
  const isProdukt = config.id === env.analysis.featureNames.produkt;
  const isKommunikation = config.id === env.analysis.featureNames.kommunikation;
  const isMassnahmen = config.id === env.analysis.featureNames.massnahmen;
  const isHandlungsempfehlungen = config.id === env.analysis.featureNames.handlungsempfehlungen;

  // Scroll indicator state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Check if there's more content to scroll
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20; // 20px threshold

    setShowScrollIndicator(isScrollable && !isAtBottom);
  }, []);

  // Set up scroll listener and initial check
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial check after content renders
    const timeoutId = setTimeout(checkScrollPosition, 100);

    container.addEventListener('scroll', checkScrollPosition, { passive: true });
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  return (
    <div
      ref={scrollContainerRef}
      className="absolute inset-0 z-20 backdrop-blur-md bg-white rounded-[var(--radius-md)] border border-[var(--glass-border)] shadow-lg flex flex-col p-6 overflow-y-auto"
    >
      {/* Close button - sticky in top right */}
      <div className="sticky top-0 flex justify-end z-10 -mb-10">
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-white/90 backdrop-blur-sm hover:bg-[var(--glass-bg)] transition-colors duration-200 group shadow-sm border border-[var(--glass-border)]"
          aria-label={t('results.closeOverlay')}
        >
          <X className="h-6 w-6 text-[var(--color-charcoal)]/50 group-hover:text-[var(--color-charcoal)] transition-colors" />
        </button>
      </div>

      {/* Custom overlay content based on card type */}
      <div className="flex-1">
        {isVertriebsgebiet && vertriebsgebietData ? (
          <VertriebsgebietOverlayContent data={vertriebsgebietData} getDescription={getDescription} featureName={config.id} />
        ) : isTargetGroup && zielgruppeData ? (
          <TargetGroupOverlayContent data={zielgruppeData} getDescription={getDescription} featureName={config.id} />
        ) : isProdukt && produktData ? (
          <ProduktOverlayContent data={produktData} getDescription={getDescription} featureName={config.id} />
        ) : isKommunikation && kommunikationData ? (
          <KommunikationOverlayContent data={kommunikationData} getDescription={getDescription} featureName={config.id} />
        ) : isMassnahmen && massnahmenData ? (
          <MassnahmenOverlayContent data={massnahmenData} getDescription={getDescription} featureName={config.id} onPremiumClick={onPremiumClick} />
        ) : isHandlungsempfehlungen && handlungsempfehlungenData ? (
          <HandlungsempfehlungenOverlayContent data={handlungsempfehlungenData} getDescription={getDescription} featureName={config.id} />
        ) : (
          <>
            {/* Fallback: Header with icon and title */}
            <div className="flex items-center gap-3 pb-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
                <Icon className="h-5 w-5 text-[var(--color-primary-start)]" />
              </div>
              <h3 className="text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">
                {title}
              </h3>
            </div>

            {/* Content */}
            <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
              <p className="text-sm text-[var(--color-charcoal)]/60 italic">
                {t('results.dataLoading')}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Scroll indicator - animated down arrow (sticky at bottom) */}
      {showScrollIndicator && (
        <div className="sticky bottom-0 flex justify-center pointer-events-none z-30 pb-2">
          <div className="animate-scroll-bounce">
            <div className="p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-md border border-[var(--glass-border)]">
              <ChevronDown className="h-5 w-5 text-[var(--color-primary-start)]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Single result card component
 */
function ResultCard({
  config,
  feature,
  isLoading,
  isPolling,
  onOpenDetail,
  t,
  vertriebsgebietData,
  zielgruppeData,
  produktData,
  kommunikationData,
  massnahmenData,
  handlungsempfehlungenData,
  getDescription,
  onPremiumClick,
}: {
  config: typeof CARD_CONFIGS[0];
  feature?: FeatureResult;
  isLoading: boolean;
  isPolling: boolean;
  onOpenDetail: () => void;
  t: (key: string, options?: Record<string, string>) => string;
  vertriebsgebietData?: VertriebsgebietFullData | null;
  zielgruppeData?: ZielgruppeFullData | null;
  produktData?: ProduktFullData | null;
  kommunikationData?: KommunikationFullData | null;
  massnahmenData?: MassnahmenFullData | null;
  handlungsempfehlungenData?: HandlungsempfehlungenFullData | null;
  getDescription?: GetDescriptionFn;
  onPremiumClick?: () => void;
}) {
  const Icon = config.icon;
  const hasData = feature && feature.value;
  const featureProgress = feature?.progressPercent ?? 0;
  const title = t(`results.cards.${config.translationKey}.title`);
  const description = t(`results.cards.${config.translationKey}.description`);
  const isVertriebsgebiet = config.id === env.analysis.featureNames.vertriebsgebiet;
  const isTargetGroup = config.id === env.analysis.featureNames.zielgruppe;
  const isProdukt = config.id === env.analysis.featureNames.produkt;
  const isKommunikation = config.id === env.analysis.featureNames.kommunikation;
  const isMassnahmen = config.id === env.analysis.featureNames.massnahmen;
  const isHandlungsempfehlungen = config.id === env.analysis.featureNames.handlungsempfehlungen;
  // Cards with dedicated parsers should never fall through to the raw text fallback
  const hasTypedParser = isVertriebsgebiet || isTargetGroup || isProdukt || isKommunikation || isMassnahmen || isHandlungsempfehlungen;
  // For typed cards, check if parsed data is actually available
  const parsedDataReady = hasTypedParser
    ? !!(vertriebsgebietData || zielgruppeData || produktData || kommunikationData || massnahmenData || handlungsempfehlungenData)
    : !!hasData;
  // Show loading when polling and parsed data isn't ready yet
  const showLoading = isLoading || (isPolling && hasTypedParser && !parsedDataReady);
  // If feature reports 100% but parsed data isn't ready, cap progress to 95%
  const effectiveProgress = (!parsedDataReady && featureProgress >= 100) ? 95 : featureProgress;

  const cardContent = (
    <GlassCard className={`h-full transition-all duration-300 hover:shadow-lg relative md:cursor-pointer ${isMassnahmen || isHandlungsempfehlungen ? '' : 'md:overflow-hidden'}`} onClick={onOpenDetail}>
      {/* Glassmorphic loading overlay */}
      {showLoading && (
        <div className="absolute inset-0 z-10 backdrop-blur-md bg-white flex flex-col items-center justify-center p-6 text-center rounded-[var(--radius-md)]">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[var(--color-primary-start)]/20 to-[var(--color-primary-end)]/20 mb-3">
            <Icon className="h-8 w-8 text-[var(--color-primary-start)]" />
          </div>
          <h3 className="font-semibold text-[var(--color-charcoal)] mb-1">{title}</h3>
          <p className="text-xs text-[var(--color-charcoal)]/60 mb-4">{t('results.dataBeingAnalyzed')}</p>
          <div className="w-full max-w-[180px]">
            <div
              role="progressbar"
              aria-valuenow={effectiveProgress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${title}: ${effectiveProgress}%`}
              className="h-1.5 bg-[var(--glass-border)] rounded-full overflow-hidden"
            >
              <div
                className="h-full gradient-bg transition-all duration-300 ease-out rounded-full"
                style={{ width: `${effectiveProgress}%` }}
              />
            </div>
            <span className="text-xs font-medium text-[var(--color-charcoal)]/70 mt-1 block">
              {effectiveProgress}%
            </span>
          </div>
        </div>
      )}

      <GlassCardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
              <Icon className="h-5 w-5 text-[var(--color-primary-start)]" />
            </div>
            <GlassCardTitle className="text-lg">{title}</GlassCardTitle>
          </div>
          <div className="hidden md:block p-2">
            <Search className="h-5 w-5 text-[var(--color-charcoal)]/50" />
          </div>
        </div>
      </GlassCardHeader>
      <GlassCardContent>
        {/* Desktop: compact card content */}
        <div className="hidden md:block">
          {/* Custom card content based on card type */}
          {isVertriebsgebiet && vertriebsgebietData ? (
            <VertriebsgebietCardContent data={vertriebsgebietData} />
          ) : isTargetGroup && zielgruppeData ? (
            <ZielgruppeCardContent data={zielgruppeData} />
          ) : isProdukt && produktData ? (
            <ProduktCardContent data={produktData} />
          ) : isKommunikation && kommunikationData ? (
            <KommunikationCardContent data={kommunikationData} />
          ) : isMassnahmen && massnahmenData ? (
            <MassnahmenCardContent data={massnahmenData} />
          ) : isHandlungsempfehlungen && handlungsempfehlungenData ? (
            <HandlungsempfehlungenCardContent data={handlungsempfehlungenData} />
          ) : hasData && !hasTypedParser ? (
            <>
              <p className="text-sm text-[var(--color-charcoal)]/70 mb-4">
                {description}
              </p>
              <div className="p-3 bg-[var(--glass-bg)] rounded-[var(--radius-sm)] border border-[var(--glass-border)]">
                <p className="text-sm text-[var(--color-charcoal)] whitespace-pre-wrap">
                  {feature.value}
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[var(--color-charcoal)]/70 mb-4">
                {description}
              </p>
              <div className="space-y-3">
                <div className="h-4 bg-[var(--glass-bg)] rounded" />
                <div className="h-4 bg-[var(--glass-bg)] rounded w-3/4" />
                <div className="h-4 bg-[var(--glass-bg)] rounded w-1/2" />
              </div>
            </>
          )}
        </div>
        {/* Mobile: show overlay content directly on card */}
        <div className="md:hidden">
          {isVertriebsgebiet && vertriebsgebietData ? (
            <VertriebsgebietOverlayContent data={vertriebsgebietData} getDescription={getDescription} featureName={config.id} />
          ) : isTargetGroup && zielgruppeData ? (
            <TargetGroupOverlayContent data={zielgruppeData} getDescription={getDescription} featureName={config.id} />
          ) : isProdukt && produktData ? (
            <ProduktOverlayContent data={produktData} getDescription={getDescription} featureName={config.id} />
          ) : isKommunikation && kommunikationData ? (
            <KommunikationOverlayContent data={kommunikationData} getDescription={getDescription} featureName={config.id} />
          ) : isMassnahmen && massnahmenData ? (
            <MassnahmenOverlayContent data={massnahmenData} getDescription={getDescription} featureName={config.id} onPremiumClick={onPremiumClick} />
          ) : isHandlungsempfehlungen && handlungsempfehlungenData ? (
            <HandlungsempfehlungenOverlayContent data={handlungsempfehlungenData} getDescription={getDescription} featureName={config.id} />
          ) : hasData && !hasTypedParser ? (
            <>
              <p className="text-sm text-[var(--color-charcoal)]/70 mb-4">
                {description}
              </p>
              <div className="p-3 bg-[var(--glass-bg)] rounded-[var(--radius-sm)] border border-[var(--glass-border)]">
                <p className="text-sm text-[var(--color-charcoal)] whitespace-pre-wrap">
                  {feature.value}
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[var(--color-charcoal)]/70 mb-4">
                {description}
              </p>
              <div className="space-y-3">
                <div className="h-4 bg-[var(--glass-bg)] rounded" />
                <div className="h-4 bg-[var(--glass-bg)] rounded w-3/4" />
                <div className="h-4 bg-[var(--glass-bg)] rounded w-1/2" />
              </div>
            </>
          )}
        </div>
      </GlassCardContent>
    </GlassCard>
  );

  // Wrap massnahmen card with animated gradient border
  if (isMassnahmen) {
    return (
      <div className="animated-gradient-border h-full">
        {cardContent}
      </div>
    );
  }

  return cardContent;
}

/**
 * Loading skeleton for the page
 */
function ResultsLoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-10 w-80 bg-[var(--glass-bg)] rounded animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="h-56 bg-[var(--glass-bg)] rounded-[var(--radius-md)] animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Analysis results page V1 (standard layout)
 */
function AnalysisResultsPageV1() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { analysisId } = useParams<{ analysisId: string }>();
  const { trackResultCard } = useFeatureTracking();
  const [openOverlayId, setOpenOverlayId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
  const [premiumStep, setPremiumStep] = useState<'info' | 'request' | 'success'>('info');
  const [ccEmails, setCcEmails] = useState<string[]>([]);

  const premiumMutation = useMutation({
    mutationFn: (ccEmails: string[]) =>
      analysesService.requestPremiumMeasures(
        ccEmails.filter((e) => e.trim() !== ''),
        analysisId
      ),
    onSuccess: () => {
      setPremiumStep('success');
    },
  });

  const {
    data,
    isLoading,
    error,
    isPolling,
    isCompleted,
    isFailed,
    progress,
    features,
    refetch,
  } = useRequestStatus(analysisId || '');

  const retryMutation = useMutation({
    mutationFn: () => requestsService.retryFailedRequest(analysisId || ''),
    onSuccess: () => {
      refetch();
    },
  });

  const { getDescription } = useSectionDescriptions(data?.productId);

  // Build feature map by name for reliable lookup (must be before early returns to follow Rules of Hooks)
  const featureMap = useMemo(() => {
    const map = new Map<string, FeatureResult>();
    features.forEach((feature) => {
      map.set(feature.featureName.toLowerCase(), feature);
    });
    return map;
  }, [features]);

  // Helper to get feature by name from environment config
  const getFeatureByName = useCallback((featureName: string): FeatureResult | undefined => {
    return featureMap.get(featureName.toLowerCase());
  }, [featureMap]);

  // Parse Vertriebsgebiet data
  const vertriebsgebietFeature = getFeatureByName(env.analysis.featureNames.vertriebsgebiet);
  const vertriebsgebietData = useMemo(() => {
    if (!vertriebsgebietFeature?.value) return null;
    return parseVertriebsgebietData(vertriebsgebietFeature.value);
  }, [vertriebsgebietFeature?.value]);

  // Parse Zielgruppe data
  const zielgruppeFeature = getFeatureByName(env.analysis.featureNames.zielgruppe);
  const zielgruppeData = useMemo(() => {
    if (!zielgruppeFeature?.value) return null;
    return parseZielgruppeData(zielgruppeFeature.value);
  }, [zielgruppeFeature?.value]);

  // Parse Produkt data
  const produktFeature = getFeatureByName(env.analysis.featureNames.produkt);
  const produktData = useMemo(() => {
    if (!produktFeature?.value) return null;
    return parseProduktData(produktFeature.value);
  }, [produktFeature?.value]);

  // Parse Kommunikation data
  const kommunikationFeature = getFeatureByName(env.analysis.featureNames.kommunikation);
  const kommunikationData = useMemo(() => {
    if (!kommunikationFeature?.value) return null;
    return parseKommunikationData(kommunikationFeature.value);
  }, [kommunikationFeature?.value]);

  // Parse Massnahmen data
  const massnahmenFeature = getFeatureByName(env.analysis.featureNames.massnahmen);
  const massnahmenData = useMemo(() => {
    if (!massnahmenFeature?.value) return null;
    return parseMassnahmenData(massnahmenFeature.value);
  }, [massnahmenFeature?.value]);

  // Parse Handlungsempfehlungen data
  const handlungsempfehlungenFeature = getFeatureByName(env.analysis.featureNames.handlungsempfehlungen);
  const handlungsempfehlungenData = useMemo(() => {
    if (!handlungsempfehlungenFeature?.value) return null;
    return parseHandlungsempfehlungenData(handlungsempfehlungenFeature.value);
  }, [handlungsempfehlungenFeature?.value]);

  // Loading state
  if (isLoading) {
    return (
      <DashboardLayout header={<DashboardHeader />} footer={<Footer />}>
        <div className="max-w-6xl mx-auto py-8">
          <p className="text-center text-[var(--color-charcoal)]/70 mb-8 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('results.loadingResults')}
          </p>
          <ResultsLoadingSkeleton />
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <DashboardLayout header={<DashboardHeader />} footer={<Footer />}>
        <div className="max-w-6xl mx-auto py-8">
          <Alert variant="destructive" className="max-w-4xl mx-auto">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error?.message || t('results.analysisNotFound')}
            </AlertDescription>
          </Alert>
          <div className="text-center mt-6">
            <Button asChild variant="outline">
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('results.backToDashboard')}
              </Link>
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Failed state
  if (isFailed) {
    const canRetry = !data.hasBeenRetried && !retryMutation.isPending && !retryMutation.isSuccess;
    return (
      <DashboardLayout header={<DashboardHeader />} footer={<Footer />}>
        <div className="max-w-6xl mx-auto py-8">
          <Alert variant="destructive" className="max-w-4xl mx-auto">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {data.lastErrorMessage || t('analysisErrors.processingFailed')}
            </AlertDescription>
          </Alert>
          {retryMutation.isError && (
            <Alert variant="destructive" className="max-w-4xl mx-auto mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('analysisErrors.retryFailed')}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-center gap-3 mt-6">
            {canRetry && (
              <Button
                variant="default"
                onClick={() => retryMutation.mutate()}
                disabled={retryMutation.isPending}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                {t('analysisErrors.retryButton')}
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('results.backToDashboard')}
              </Link>
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout header={<DashboardHeader />} footer={<Footer />}>
      <div className="max-w-6xl mx-auto pt-0 pb-8">
        {/* Page headline */}
        <div className="mb-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="gradient-text text-3xl md:text-4xl font-bold">
              {t('results.pageTitle')}
            </h1>
            <Button
              variant="outline"
              size="sm"
              data-track="share_open"
              className="rounded-full border-[var(--color-primary-start)]/30 hover:border-[var(--color-primary-start)] hover:bg-[var(--color-primary-start)]/5"
              onClick={() => setShareModalOpen(true)}
              disabled={!isCompleted}
            >
              <Share2 className="h-4 w-4 mr-2 text-[var(--color-primary-start)]" />
              {t('results.share.button')}
            </Button>
          </div>
          {data.createdAt && (
            <p className="text-[var(--color-charcoal)]/70 mt-2">
              {t('results.createdAt', {
                date: new Date(data.createdAt).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              })}
              {isCompleted && data.finishedAt && (
                <> &middot; {t('results.completedAt', {
                  date: new Date(data.finishedAt).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                })}</>
              )}
            </p>
          )}
        </div>

        {/* Progress banner - below headline while processing */}
        <ProgressBanner progress={progress} isPolling={isPolling} productId={data?.productId} />

        {/* Result cards container - with overlay support */}
        <div className="relative mt-8">
          {/* Customer Journey card - full width */}
          {(() => {
            const massnahmenConfig = CARD_CONFIGS.find(c => c.id === env.analysis.featureNames.massnahmen);
            if (!massnahmenConfig) return null;
            const feature = featureMap.get(massnahmenConfig.id.toLowerCase());
            return (
              <ResultCard
                config={massnahmenConfig}
                feature={feature}
                isLoading={isPolling && !feature?.value}
                isPolling={isPolling}
                onOpenDetail={() => { trackResultCard(massnahmenConfig.translationKey, 'opened'); setOpenOverlayId(massnahmenConfig.id); }}
                t={t}
                massnahmenData={massnahmenData}
                getDescription={getDescription}
                onPremiumClick={() => { setPremiumStep('info'); setCcEmails([]); premiumMutation.reset(); setPremiumDialogOpen(true); }}
              />
            );
          })()}

          {/* Section headline */}
          <h2 className="text-xl md:text-2xl font-semibold text-[var(--color-charcoal)] mt-8 mb-2">
            {t('results.detailResultsHeadline')}
          </h2>

          {/* Detail cards grid - 2 per row */}
          <div className="grid grid-cols-1 md:grid-cols-2 md:auto-rows-[1fr] gap-6">
            {CARD_CONFIGS.filter(config => config.id !== env.analysis.featureNames.handlungsempfehlungen && config.id !== env.analysis.featureNames.massnahmen).map((config) => {
              // Match feature by name using the feature map
              const feature = featureMap.get(config.id.toLowerCase());
              return (
                <div key={config.id}>
                <ResultCard
                  config={config}
                  feature={feature}
                  isLoading={isPolling && !feature?.value}
                  isPolling={isPolling}
                  onOpenDetail={() => { trackResultCard(config.translationKey, 'opened'); setOpenOverlayId(config.id); }}
                  t={t}
                  vertriebsgebietData={config.id === env.analysis.featureNames.vertriebsgebiet ? vertriebsgebietData : undefined}
                  zielgruppeData={config.id === env.analysis.featureNames.zielgruppe ? zielgruppeData : undefined}
                  produktData={config.id === env.analysis.featureNames.produkt ? produktData : undefined}
                  kommunikationData={config.id === env.analysis.featureNames.kommunikation ? kommunikationData : undefined}
                  handlungsempfehlungenData={config.id === env.analysis.featureNames.handlungsempfehlungen ? handlungsempfehlungenData : undefined}
                  getDescription={getDescription}
                  onPremiumClick={() => { setPremiumStep('info'); setCcEmails([]); premiumMutation.reset(); setPremiumDialogOpen(true); }}
                />
                </div>
              );
            })}
          </div>

          {/* Detail overlay - covers all cards (desktop only) */}
          <div className="hidden md:block">
            {openOverlayId && (() => {
              const config = CARD_CONFIGS.find(c => c.id === openOverlayId);
              const feature = featureMap.get(openOverlayId.toLowerCase());
              if (!config) return null;
              return (
                <DetailOverlay
                  key={openOverlayId}
                  config={config}
                  feature={feature}
                  onClose={() => { trackResultCard(config.translationKey, 'closed'); setOpenOverlayId(null); }}
                  t={t}
                  vertriebsgebietData={openOverlayId === env.analysis.featureNames.vertriebsgebiet ? vertriebsgebietData : undefined}
                  zielgruppeData={openOverlayId === env.analysis.featureNames.zielgruppe ? zielgruppeData : undefined}
                  produktData={openOverlayId === env.analysis.featureNames.produkt ? produktData : undefined}
                  kommunikationData={openOverlayId === env.analysis.featureNames.kommunikation ? kommunikationData : undefined}
                  massnahmenData={openOverlayId === env.analysis.featureNames.massnahmen ? massnahmenData : undefined}
                  handlungsempfehlungenData={openOverlayId === env.analysis.featureNames.handlungsempfehlungen ? handlungsempfehlungenData : undefined}
                  getDescription={getDescription}
                  onPremiumClick={() => { setPremiumStep('info'); setCcEmails([]); premiumMutation.reset(); setPremiumDialogOpen(true); }}
                />
              );
            })()}
          </div>
        </div>

        {/* CTA Button - Premium Analyse Freischalten */}
        <section className="mt-8 pt-8 border-t border-[var(--glass-border)]">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-[var(--color-charcoal)] mb-2">{t('results.wantDeeperInsights')}</h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">{t('results.premiumDescription')}</p>
          </div>
          <div className="text-center">
            <button
              onClick={() => { setPremiumStep('info'); setCcEmails([]); premiumMutation.reset(); setPremiumDialogOpen(true); }}
              className="btn-primary text-base md:text-lg px-8 py-3 rounded-full inline-flex items-center gap-2 transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <LockOpen className="h-5 w-5" />
              {t('results.upgradeToPremium')}
            </button>
          </div>
        </section>
      </div>

      {/* Share Analysis Modal */}
      <ShareAnalysisModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        correlationId={analysisId || ''}
      />

      {/* Premium Analysis Dialog */}
      <DialogPrimitive.Root open={premiumDialogOpen} onOpenChange={(open) => {
        setPremiumDialogOpen(open);
        if (!open) { setPremiumStep('info'); setCcEmails([]); premiumMutation.reset(); }
      }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            aria-describedby="premium-dialog-description"
            className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-lg)] border border-[var(--glass-border)] bg-white p-6 shadow-[var(--glass-shadow)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            style={{ width: 'min(28rem, calc(100vw - 2rem))', maxHeight: '90dvh', overflowY: 'auto' }}
          >
            {/* Close button */}
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-start)] focus:ring-offset-2">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>

            {premiumStep === 'info' && (
              <>
                {/* Header */}
                <div className="flex flex-col items-center text-center space-y-1.5">
                  <div className="mb-3">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--color-primary-start)]/20 to-[var(--color-primary-end)]/20 flex items-center justify-center">
                      <Crown className="h-7 w-7 text-[var(--color-primary-start)]" />
                    </div>
                  </div>
                  <DialogPrimitive.Title className="text-xl font-semibold text-[var(--color-charcoal)]">
                    {t('results.premiumDialogTitle')}
                  </DialogPrimitive.Title>
                  <span className="inline-flex items-center rounded-full gradient-bg px-3 py-1 text-xs font-semibold text-white shadow-sm">
                    20 % Rabatt bis zum 30.04.2026
                  </span>
                  <DialogPrimitive.Description id="premium-dialog-description" className="text-sm text-[var(--color-charcoal)]/70 mt-2 leading-relaxed">
                    {t('results.premiumDialogIntro', { date: '30.04.2026' })}
                  </DialogPrimitive.Description>
                </div>

                {/* Pricing */}
                <div className="mt-4 rounded-[var(--radius-md)] border border-[var(--glass-border)] bg-[#f5f5f7] p-4 space-y-2 text-sm text-[var(--color-charcoal)]">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="shrink-0">{t('results.premiumDialogRegularPrice')}</span>
                    <span className="font-semibold line-through opacity-50 whitespace-nowrap">2.450,00&nbsp;€</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-medium shrink-0">{t('results.premiumDialogActionPrice')}</span>
                    <span className="font-bold text-[var(--color-primary-start)] whitespace-nowrap">1.960,00&nbsp;€</span>
                  </div>
                  <p className="text-xs text-[var(--color-charcoal)]/60 pt-1">
                    {t('results.premiumDialogDiscount', { date: '30.04.2026' })}
                  </p>
                </div>

                {/* Collapsible "Erfahren Sie mehr" */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById('premium-details-v1');
                      el?.toggleAttribute('data-open');
                    }}
                    className="flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary-start)] hover:opacity-80 transition-opacity mx-auto"
                  >
                    <span>Erfahren Sie mehr</span>
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-open]_&]:rotate-180" />
                  </button>
                  <div
                    id="premium-details-v1"
                    className="grid transition-all duration-300 ease-in-out grid-rows-[0fr] opacity-0 [&[data-open]]:grid-rows-[1fr] [&[data-open]]:opacity-100"
                  >
                    <div className="overflow-hidden">
                      <ul className="mt-3 space-y-2 text-sm text-[var(--color-charcoal)]/80 text-left">
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 text-[var(--color-primary-start)]">&#10003;</span>
                          <span>Tiefgehende Markt- und Wettbewerbsanalyse mit aktuellen Daten</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 text-[var(--color-primary-start)]">&#10003;</span>
                          <span>Individuelle Handlungsempfehlungen für Ihr Unternehmen</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 text-[var(--color-primary-start)]">&#10003;</span>
                          <span>Detaillierte Zielgruppenprofile und Kommunikationsstrategien</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 text-[var(--color-primary-start)]">&#10003;</span>
                          <span>Konkrete Maßnahmenpläne mit Umsetzungsprioritäten</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 text-[var(--color-primary-start)]">&#10003;</span>
                          <span>Persönliche Beratung durch unsere Analysten</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Action */}
                <div className="mt-6 pt-4 border-t border-[var(--glass-border)]">
                  <Button className="w-full btn-primary" onClick={() => setPremiumStep('request')}>
                    {t('results.premiumDialogRequestOffer')}
                  </Button>
                </div>
              </>
            )}

            {premiumStep === 'request' && (
              <form onSubmit={(e) => { e.preventDefault(); premiumMutation.mutate(ccEmails); }}>
                {/* Back button */}
                <button
                  type="button"
                  onClick={() => setPremiumStep('info')}
                  disabled={premiumMutation.isPending}
                  className="flex items-center gap-1 text-sm text-[var(--color-charcoal)]/60 hover:text-[var(--color-charcoal)] transition-colors disabled:opacity-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Zurück</span>
                </button>

                {/* Header */}
                <div className="flex flex-col items-center text-center space-y-1.5">
                  <div className="mb-3">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--color-primary-start)]/20 to-[var(--color-primary-end)]/20 flex items-center justify-center">
                      <Mail className="h-7 w-7 text-[var(--color-primary-start)]" />
                    </div>
                  </div>
                  <DialogPrimitive.Title className="text-xl font-semibold text-[var(--color-charcoal)]">
                    Unverbindliches Angebot anfordern
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-[var(--color-charcoal)]/70 mt-2 leading-relaxed">
                    Wir senden Ihnen ein unverbindliches Angebot per E-Mail zu. Sie können weitere Empfänger hinzufügen, die das Angebot ebenfalls erhalten sollen.
                  </DialogPrimitive.Description>
                </div>

                {/* User email (read-only) */}
                <div className="mt-4 space-y-2">
                  <Label className="text-[var(--color-charcoal)] font-medium">Empfänger</Label>
                  <Input
                    type="email"
                    value={user?.email ?? ''}
                    disabled
                    className="bg-[#f5f5f7] border-[var(--glass-border)] text-[var(--color-charcoal)]/70 cursor-not-allowed"
                  />
                </div>

                {/* CC Emails */}
                <div className="mt-4 space-y-3">
                  <Label className="text-[var(--color-charcoal)] font-medium">
                    Weitere Empfänger (optional)
                  </Label>
                  <div id="premium-cc-list-v1" className="space-y-2">
                    {ccEmails.map((email, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="email"
                          required
                          placeholder="name@firma.de"
                          value={email}
                          disabled={premiumMutation.isPending}
                          onChange={(e) => {
                            const updated = [...ccEmails];
                            updated[index] = e.target.value;
                            setCcEmails(updated);
                          }}
                          className="bg-white/50 border-[var(--glass-border)] focus:border-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={premiumMutation.isPending}
                          onClick={() => setCcEmails(ccEmails.filter((_, i) => i !== index))}
                          className="shrink-0 text-[var(--color-charcoal)]/40 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    disabled={premiumMutation.isPending}
                    onClick={() => {
                      setCcEmails([...ccEmails, '']);
                      requestAnimationFrame(() => {
                        const inputs = document.querySelectorAll<HTMLInputElement>('#premium-cc-list-v1 input[type="email"]');
                        inputs[inputs.length - 1]?.focus();
                      });
                    }}
                    className="flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary-start)] hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Empfänger hinzufügen</span>
                  </button>
                </div>

                {/* Error message */}
                {premiumMutation.isError && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.</span>
                  </div>
                )}

                {/* Action */}
                <div className="mt-6 pt-4 border-t border-[var(--glass-border)]">
                  <Button type="submit" disabled={premiumMutation.isPending} className="w-full btn-primary gap-2">
                    {premiumMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Wird gesendet…
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Angebot jetzt anfordern
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {premiumStep === 'success' && (
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-green-600" />
                </div>
                <DialogPrimitive.Title className="text-xl font-semibold text-[var(--color-charcoal)]">
                  Anfrage erfolgreich gesendet
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-sm text-[var(--color-charcoal)]/70 leading-relaxed">
                  Wir haben Ihre Anfrage erhalten und senden Ihnen in Kürze ein unverbindliches Angebot per E-Mail zu.
                </DialogPrimitive.Description>
                <div className="pt-4 w-full">
                  <Button className="w-full btn-primary" onClick={() => { setPremiumStep('info'); setCcEmails([]); premiumMutation.reset(); setPremiumDialogOpen(false); }}>
                    Schließen
                  </Button>
                </div>
              </div>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </DashboardLayout>
  );
}

// Lazy load V2 page for code splitting
const AnalysisResultsPageV2 = lazy(() => import('@/pages/AnalysisResultsV2'));

/**
 * Loading fallback for V2 page lazy loading
 */
function V2LoadingFallback() {
  const { t } = useTranslation();
  return (
    <DashboardLayout header={<DashboardHeader />} footer={<Footer />}>
      <div className="max-w-6xl mx-auto py-8">
        <p className="text-center text-[var(--color-charcoal)]/70 mb-8 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t('results.loadingResults')}
        </p>
        <ResultsLoadingSkeleton />
      </div>
    </DashboardLayout>
  );
}

/**
 * Analysis Results Router
 * Routes between V1 and V2 analysis pages based on the analysisType payload parameter
 * The routing is invisible to the user - same URL renders different content
 */
export default function AnalysisResultsRouter() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const { analysisVariant, isLoading } = useRequestStatus(analysisId || '');

  // While loading, we don't know the variant yet - show loading state
  // React Query will cache this, so V1/V2 pages won't re-fetch
  if (isLoading) {
    return <V2LoadingFallback />;
  }

  // Route to appropriate page based on variant
  if (analysisVariant === 'v2') {
    return (
      <Suspense fallback={<V2LoadingFallback />}>
        <AnalysisResultsPageV2 />
      </Suspense>
    );
  }

  // Default to V1
  return <AnalysisResultsPageV1 />;
}

// Export V1 as named export for direct access if needed
export { AnalysisResultsPageV1 };
